// lib/grok-imagine-2.ts
import type { GrokImagine2AspectRatio, GrokImagine2Resolution } from '#types/types';

export const GROK_2_SUPPORTED_RATIOS: Record<Exclude<GrokImagine2AspectRatio, 'auto'>, number> = {
  '2:1': 2, '20:9': 20 / 9, '19.5:9': 19.5 / 9, '16:9': 16 / 9, '4:3': 4 / 3,
  '3:2': 3 / 2, '1:1': 1, '2:3': 2 / 3, '3:4': 3 / 4, '9:16': 9 / 16,
  '9:19.5': 9 / 19.5, '9:20': 9 / 20, '1:2': 1 / 2,
};

export const GROK_IMAGINE_2_MAX_INPUT_IMAGES = 3;

/** Grok 2 has no 4K tier; accepting one would send an unsupported value to fal. */
export const resolveGrokImagine2Resolution = (value: unknown): GrokImagine2Resolution | null => {
  const resolution = typeof value === 'string' ? value.trim().toUpperCase() : value;
  switch (resolution) {
    case undefined:
    case null:
    case '':
      return '2K';
    case '1K':
    case '2K':
      return resolution;
    default:
      return null;
  }
};

export const getGrokImagine2Endpoint = (hasSource: boolean): string => (
  `xai/grok-imagine-image/v2.0/${hasSource ? 'edit' : 'text-to-image'}`
);

export const buildGrokImagine2ProviderInput = (input: {
  prompt: string;
  resolution: GrokImagine2Resolution;
  aspectRatio: GrokImagine2AspectRatio;
  imageUrls?: readonly string[];
}) => {
  const imageUrls = (input.imageUrls ?? [])
    .filter((url, index, urls): url is string => typeof url === 'string' && url.trim().length > 0 && urls.indexOf(url) === index)
    .slice(0, GROK_IMAGINE_2_MAX_INPUT_IMAGES);
  return {
    prompt: input.prompt,
    // Studio owns batching (one task per variant), so the provider always returns a single image.
    num_images: 1,
    // 'auto' only exists on the edit endpoint; text-to-image must receive one of the 13 shapes.
    aspect_ratio: input.aspectRatio in GROK_2_SUPPORTED_RATIOS
      ? input.aspectRatio
      : imageUrls.length > 0 ? 'auto' : '1:1',
    resolution: input.resolution.toLowerCase(),
    quality: 'medium' as const,
    output_format: 'webp' as const,
    // A data-URI response would blow up the fal.subscribe payload.
    sync_mode: false,
    ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
  };
};
